import { useEffect, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Plus, PenLine, ChevronRight, ChevronLeft, Eye, Search, X } from 'lucide-react';
import { cn, Button } from '@jeraisonne/ui';
import {
  type BlogArticleSummary,
  blogStatusLabel,
  blogArticleTags,
  blogPageCount,
} from '@jeraisonne/db';
import { useAdminBlogArticles } from '@/lib/admin/use-blog';

// Style du badge dérivé du libellé d'état (tri-état visibilité + status éditorial).
const LABEL_STYLES: Record<string, string> = {
  Publié: 'bg-xp-100 text-xp-700',
  'En attente de validation': 'bg-level-50 text-level-600',
  Archivé: 'bg-accent-50 text-accent-600',
  Brouillon: 'bg-ink-100 text-ink-600',
};

function BlogRow({ article }: { article: BlogArticleSummary }) {
  const isPublic = article.visibility === 'public' && article.status === 'published';
  const gen = article.generation_status;
  const statusLabel = blogStatusLabel(article.visibility, article.status);
  const tags = blogArticleTags(article.seo, 3);
  return (
    <Link
      to="/tutor/blog/$articleId"
      params={{ articleId: article.id }}
      className="hover:bg-ink-50 group flex items-center gap-3 rounded-xl p-3 transition-colors"
    >
      <div className="bg-level-50 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg">
        <PenLine className="text-level-500 h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-display text-ink-900 truncate text-[14px] font-semibold">
          {article.title}
        </p>
        <div className="mt-0.5 flex flex-wrap items-center gap-1.5">
          <span className="text-ink-500 font-sans text-[11px]">
            {article.reading_time} min de lecture
          </span>
          {isPublic && (
            <>
              <span className="text-ink-300">·</span>
              <span className="text-ink-500 inline-flex items-center gap-0.5 font-sans text-[11px]">
                <Eye className="h-3 w-3" />
                {article.view_count}
              </span>
            </>
          )}
        </div>
        {tags.length > 0 && (
          <div className="mt-1 flex flex-wrap gap-1">
            {tags.map((tag) => (
              <span
                key={tag}
                className="bg-primary-50 text-primary-700 rounded-full px-1.5 py-0.5 text-[10px] font-medium"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      {gen === 'generating' ? (
        <span className="bg-level-50 text-level-600 font-display inline-flex shrink-0 items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold">
          <span className="border-level-500 h-2.5 w-2.5 animate-spin rounded-full border border-t-transparent" />
          Génération…
        </span>
      ) : gen === 'failed' ? (
        <span className="bg-accent-50 text-accent-600 font-display shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold">
          Échec génération
        </span>
      ) : (
        <span
          className={cn(
            'font-display shrink-0 rounded-full px-2 py-0.5 text-[11px] font-semibold',
            LABEL_STYLES[statusLabel] ?? 'bg-ink-100 text-ink-600',
          )}
        >
          {statusLabel}
        </span>
      )}
      <ChevronRight className="text-ink-300 group-hover:text-ink-500 h-4 w-4 shrink-0 transition-colors" />
    </Link>
  );
}

export function TutorBlogPage() {
  const [searchInput, setSearchInput] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  // Recherche débouncée (300 ms) ; toute nouvelle requête revient en page 1.
  useEffect(() => {
    const t = setTimeout(() => {
      setQ(searchInput.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const { data, isLoading } = useAdminBlogArticles({ q, page });
  const articles = data?.articles ?? [];
  const total = data?.total ?? 0;
  const pageCount = blogPageCount(total);

  return (
    <div className="mx-auto max-w-3xl p-4 sm:p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-ink-900 text-[22px] font-extrabold">Blog réflexif</h1>
          <p className="text-ink-500 mt-0.5 font-sans text-[13px]">
            {total} article{total !== 1 ? 's' : ''}
            {q ? ' trouvé' + (total !== 1 ? 's' : '') : ' — « Solve Path de la réflexion »'}
          </p>
        </div>
        <Link to="/tutor/blog/new">
          <Button size="sm" className="flex items-center gap-1.5">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">Nouvel article</span>
            <span className="sm:hidden">Nouveau</span>
          </Button>
        </Link>
      </div>

      {/* Recherche par titre, contenu ou mot-clé (search_text, migration 0139). */}
      <div className="relative mb-4">
        <Search className="text-ink-400 pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          type="search"
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          placeholder="Rechercher par titre, contenu, mot-clé…"
          aria-label="Rechercher un article"
          className="border-ink-200 text-ink-900 placeholder:text-ink-400 focus:ring-primary-400 min-h-11 w-full rounded-xl border bg-white pl-9 pr-9 font-sans text-[14px] focus:outline-none focus:ring-2"
        />
        {searchInput && (
          <button
            type="button"
            onClick={() => setSearchInput('')}
            aria-label="Effacer la recherche"
            className="text-ink-400 hover:text-ink-700 absolute right-2 top-1/2 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-lg"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="border-primary-500 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
        </div>
      ) : articles.length === 0 ? (
        <div className="py-16 text-center">
          <PenLine className="text-ink-300 mx-auto mb-3 h-10 w-10" />
          <p className="font-display text-ink-600 font-semibold">
            {q ? 'Aucun résultat' : 'Aucun article'}
          </p>
          <p className="text-ink-400 mt-1 font-sans text-[13px]">
            {q
              ? 'Aucun article ne correspond à cette recherche.'
              : 'Générez votre premier parcours de réflexion à partir d’un sujet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="border-ink-200 divide-ink-100 divide-y overflow-hidden rounded-2xl border bg-white">
            {articles.map((a) => (
              <BlogRow key={a.id} article={a} />
            ))}
          </div>

          {/* Pagination — 10 articles par page. */}
          {pageCount > 1 && (
            <div className="mt-4 flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                className="min-h-11"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <ChevronLeft className="h-4 w-4" /> Précédent
              </Button>
              <span className="text-ink-500 font-sans text-[13px]">
                Page {Math.min(page, pageCount)} / {pageCount}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="min-h-11"
                disabled={page >= pageCount}
                onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
              >
                Suivant <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
